// Session RC1: the rate card's words and arithmetic on the office screens, one home. Mirrors of the server's own helpers in
// server/src/modules/subcontractorRates/rateCard.ts (upliftLabel, clockLabel, labourOnlyPence, RC1_SETTING_DEFAULTS), so
// the office reads exactly what Schedule 1, Your Rates and the pay engine print. rateCardUi.test.ts pins the parity.
//
// AN UPLIFT IS NEVER A BARE PERCENTAGE on screen: "Normal rate", "Time and a half (x1.5)", "Double time (x2)", else
// "x1.25". The editor still stores the whole percentage the server takes (150 is time and a half).
import type { RateCardFigures, RateCardLineKey, StandardCardSettings } from "./api";

export const RC1_SETTING_DEFAULTS = {
  weekdayEveningMultiplierPct: 100,
  weekdayEveningStartMins: 1080,
  bankHolidayMultiplierPct: 100,
  techVisitBlockMins: 300,
} as const;

/** Every line of the card in the order the office reads it, with its plain unit. */
export const RATE_LINES: { key: RateCardLineKey; label: string; unit: string }[] = [
  { key: "calloutFirstHourPence", label: "Call-out, first hour", unit: "per hour" },
  { key: "repairHourPence", label: "Repair", unit: "per hour" },
  { key: "dayRatePence", label: "Day rate", unit: "per day" },
  { key: "techVisitHourPence", label: "Technician rate", unit: "per hour" },
  { key: "techVisitBlockPence", label: "Technician visit block", unit: "per visit" },
  { key: "travelHourPence", label: "Travel", unit: "per hour" },
  { key: "servicingPerVisitPence", label: "Servicing visit", unit: "per visit" },
  { key: "siteSurveyPerVisitPence", label: "Site survey", unit: "per visit" },
  { key: "standbyPerWeekPence", label: "Standby retainer", unit: "per week" },
  { key: "modernisationPerWeekPence", label: "Modernisation or installation", unit: "per week per engineer" },
];

/** The uplift in words (the server's upliftLabel). */
export function upliftWords(pct: number): string {
  if (pct === 100) return "Normal rate";
  if (pct === 150) return "Time and a half (x1.5)";
  if (pct === 200) return "Double time (x2)";
  return `x${(pct / 100).toFixed(2).replace(/\.?0+$/, "")}`;
}

/** The three uplifts the editor offers, in words; a card holding another figure keeps it as a fourth option. */
export const UPLIFT_CHOICES = [100, 150, 200] as const;

/** Minutes after midnight as a 24-hour clock: 1080 is "18:00". */
export function clockText(mins: number): string {
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

/** "18:00" to 1080; null for anything that is not a 24-hour time. */
export function clockToMins(text: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h > 23 || mm > 59) return null;
  return h * 60 + mm;
}

/** The Labour Only figure: the Bona Fide figure less the deduction, rounded to the penny (the server's labourOnlyPence). */
export function labourOnlyPence(bonaFidePence: number | null | undefined, deductionBps: number | null | undefined): number | null {
  if (bonaFidePence == null) return null;
  if (deductionBps == null || deductionBps <= 0) return bonaFidePence;
  return Math.round((bonaFidePence * (10_000 - deductionBps)) / 10_000);
}

/** "GBP 81.50", the same words the server's penceLabel prints; null is "Not agreed". */
export function penceText(pence: number | null | undefined): string {
  if (pence == null) return "Not agreed";
  const abs = Math.abs(pence);
  return `${pence < 0 ? "-" : ""}GBP ${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

/** "5 hours", "8h 30m" (the server's thresholdLabel). */
export function hoursText(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hour${h === 1 ? "" : "s"}` : `${h}h ${String(m).padStart(2, "0")}m`;
}

export type UpliftSettings = Pick<StandardCardSettings, "saturdayMultiplierPct" | "sundayMultiplierPct"> &
  Partial<Pick<StandardCardSettings, "weekdayEveningMultiplierPct" | "weekdayEveningStartMins" | "bankHolidayMultiplierPct">>;

/** The uplift bands in words, in the order a subcontractor reads them. Sunday and bank holidays share a row when equal. */
export function upliftBands(s: UpliftSettings): { when: string; words: string }[] {
  const start = clockText(s.weekdayEveningStartMins ?? RC1_SETTING_DEFAULTS.weekdayEveningStartMins);
  const evening = s.weekdayEveningMultiplierPct ?? RC1_SETTING_DEFAULTS.weekdayEveningMultiplierPct;
  const bank = s.bankHolidayMultiplierPct ?? RC1_SETTING_DEFAULTS.bankHolidayMultiplierPct;
  const rows = [
    { when: `Weekdays before ${start}`, words: upliftWords(100) },
    { when: `Weekdays from ${start}`, words: upliftWords(evening) },
    { when: "Saturday", words: upliftWords(s.saturdayMultiplierPct) },
  ];
  if (s.sundayMultiplierPct === bank) rows.push({ when: "Sunday and bank holidays", words: upliftWords(bank) });
  else rows.push({ when: "Sunday", words: upliftWords(s.sundayMultiplierPct) }, { when: "Bank holidays", words: upliftWords(bank) });
  return rows;
}

/** The deduction in words: "10% off every Bona Fide figure", or not set. */
export function deductionText(bps: number | null | undefined): string {
  if (bps == null) return "Labour Only deduction not set: the Labour Only figures are the Bona Fide figures.";
  return `Labour Only is ${(bps / 100).toFixed(2).replace(/\.?0+$/, "")}% off every Bona Fide figure, rounded to the penny.`;
}

/** Today in London as YYYY-MM-DD (the server's londonDayKey), for an effective date's default and its min. */
export function londonToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

// ---- The figures form shared by the custom rates at registration and a new standard card version ----

export interface RateFiguresForm {
  figures: Record<RateCardLineKey, string>;
  dailyCapThresholdMins: string;
  saturdayMultiplierPct: string;
  sundayMultiplierPct: string;
  weekdayEveningMultiplierPct: string;
  weekdayEveningStart: string;
  bankHolidayMultiplierPct: string;
  techVisitBlockMins: string;
  labourOnlyDeductionPct: string;
  travelCapPerLegMins: string;
}

export function penceToPoundsText(pence: number | null | undefined): string {
  if (pence == null) return "";
  return `${Math.floor(pence / 100)}.${String(pence % 100).padStart(2, "0")}`;
}

export function poundsTextToPence(text: string): number | null | undefined {
  const t = text.trim();
  if (t === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return undefined;
  const [whole, frac = ""] = t.split(".");
  return Number(whole) * 100 + Number((frac + "00").slice(0, 2));
}

export function bpsText(bps: number | null | undefined): string {
  if (bps == null) return "";
  return `${(bps / 100).toFixed(2).replace(/\.?0+$/, "")}`;
}

export function percentTextToBpsValue(text: string): number | null | undefined {
  const t = text.trim().replace(/%$/, "").trim();
  if (t === "") return null;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(t)) return undefined;
  const n = Math.round(Number(t) * 100);
  return n > 10_000 ? undefined : n;
}

type FigureSource = Partial<RateCardFigures> & Partial<StandardCardSettings>;

export function rateFormFrom(src: FigureSource | null): RateFiguresForm {
  const figures = Object.fromEntries(RATE_LINES.map((l) => [l.key, penceToPoundsText(src?.[l.key] ?? null)])) as Record<RateCardLineKey, string>;
  return {
    figures,
    dailyCapThresholdMins: String(src?.dailyCapThresholdMins ?? 480),
    saturdayMultiplierPct: String(src?.saturdayMultiplierPct ?? 100),
    sundayMultiplierPct: String(src?.sundayMultiplierPct ?? 100),
    weekdayEveningMultiplierPct: String(src?.weekdayEveningMultiplierPct ?? RC1_SETTING_DEFAULTS.weekdayEveningMultiplierPct),
    weekdayEveningStart: clockText(src?.weekdayEveningStartMins ?? RC1_SETTING_DEFAULTS.weekdayEveningStartMins),
    bankHolidayMultiplierPct: String(src?.bankHolidayMultiplierPct ?? RC1_SETTING_DEFAULTS.bankHolidayMultiplierPct),
    techVisitBlockMins: String((src?.techVisitBlockMins ?? RC1_SETTING_DEFAULTS.techVisitBlockMins) / 60),
    labourOnlyDeductionPct: bpsText(src?.labourOnlyDeductionBps ?? null),
    travelCapPerLegMins: src?.travelCapPerLegMins == null ? "" : String(src.travelCapPerLegMins),
  };
}

export type RateFiguresBody = Partial<RateCardFigures> & StandardCardSettings;

/** The request body from the form, or the first problem in words. */
export function rateBodyFromForm(form: RateFiguresForm): { body: RateFiguresBody } | { error: string } {
  const body = {} as RateFiguresBody;
  for (const l of RATE_LINES) {
    const p = poundsTextToPence(form.figures[l.key]);
    if (p === undefined) return { error: `${l.label}: enter pounds and pence, for example 62.50, or leave it blank for not agreed.` };
    body[l.key] = p;
  }
  const threshold = Number(form.dailyCapThresholdMins);
  if (!Number.isInteger(threshold) || threshold < 1 || threshold > 1440) return { error: "The day cap covers a number of minutes (480 is 8 hours)." };
  body.dailyCapThresholdMins = threshold;
  for (const [k, label] of [
    ["saturdayMultiplierPct", "Saturday"],
    ["sundayMultiplierPct", "Sunday"],
    ["weekdayEveningMultiplierPct", "Weekday evening"],
    ["bankHolidayMultiplierPct", "Bank holiday"],
  ] as const) {
    const n = Number(form[k]);
    if (!Number.isInteger(n) || n < 100 || n > 1000) return { error: `Choose the ${label} uplift.` };
    body[k] = n;
  }
  const start = clockToMins(form.weekdayEveningStart);
  if (start == null) return { error: "Enter the time the weekday evening uplift starts, for example 18:00." };
  body.weekdayEveningStartMins = start;
  const blockHours = Number(form.techVisitBlockMins);
  const blockMins = Math.round(blockHours * 60);
  if (!Number.isFinite(blockHours) || blockMins < 15 || blockMins > 1440 || blockMins % 15 !== 0) return { error: "The technician block covers a number of hours on site, in quarter hours (5 is five hours)." };
  body.techVisitBlockMins = blockMins;
  const bps = percentTextToBpsValue(form.labourOnlyDeductionPct);
  if (bps === undefined) return { error: "The Labour Only deduction is a percentage off the Bona Fide figures, for example 10, or blank for not set." };
  body.labourOnlyDeductionBps = bps;
  const cap = form.travelCapPerLegMins.trim();
  if (cap === "") body.travelCapPerLegMins = null;
  else {
    const n = Number(cap);
    if (!Number.isInteger(n) || n < 15 || n > 600) return { error: "The travel cap is the most minutes one leg is paid for, from 15 to 600 (60 is one hour), or blank for no cap." };
    body.travelCapPerLegMins = n;
  }
  return { body };
}
